<template>
  <v-container>
    <v-data-table
      style="max-height: 90vh"
      :items="showAggregated ? aggregatedNumberList : numberBlockList"
      :headers="showAggregated ? aggregatedTableHeaders : tableHeaders"
      :show-expand="showAggregated"
      :expanded.sync="tableExpanded"
      :search="tableSearchTerm"
      :custom-filter="ndcExtendedSearch"
      :sort-by="
        showAggregated ? 'amountFreePercentage' : 'nationalDestinationCode'
      "
      item-key="key"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <div
            class="pa-2 d-flex justify-space-between align-center"
            style="width: 100%"
          >
            <v-switch
              v-model="showAggregated"
              class="mt-6"
              style="display: flex; justify-content: center"
              label="Nach Vorwahl gruppieren"
            ></v-switch>
            <v-text-field
              v-model="tableSearchTerm"
              placeholder="Suche"
              class="mt-5 px-9"
            />
            <v-btn
              :disabled="!isDownloadPossible"
              small
              icon
              @click="downloadCurrentTable"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </div>
        </v-toolbar>
      </template>
      <template v-slot:[`item.subscriberNumberStart`]="{ item }">
        <sub>{{ item.nationalDestinationCode }}</sub>
        {{ item.subscriberNumberStart }}
      </template>
      <template v-slot:[`item.subscriberNumberEnd`]="{ item }">
        <sub>{{ item.nationalDestinationCode }}</sub>
        {{ item.subscriberNumberEnd }}
      </template>
      <template v-slot:[`item.amountFreePercentage`]="{ item }">
        <span :style="{ color: percentColor(item.amountFreePercentage) }">{{
          item.amountFreePercentage
        }}</span>
      </template>
      <template v-if="showAggregated" v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          Blöcke ({{ item.subscriberNumbers.length }}):
          <span
            v-for="number in item.subscriberNumbers"
            :key="number.subscriberNumber"
          >
            <br />{{ number.subscriberNumber }} -
            {{ number.subscriberNumberEnd }}
          </span>
        </td>
      </template>
    </v-data-table>
  </v-container>
</template>
<script>
import { HTTP } from '@/main/httpClient.js';
import {
  convertObjectArrayToCsv,
  downloadStringAsFile
} from '@/main/helper.js';
import NotificationObject from '@/main/NotificationObject.js';

export default {
  name: 'OcsNumberBlockList',
  components: {},
  props: {},
  data: () => ({
    showAggregated: false,
    numberBlockList: [],
    filteredBlockList: [],
    tableExpanded: [],
    tableSearchTerm: '',
    tableHeaders: [
      { text: 'Vorwahl', value: 'nationalDestinationCode', filterable: true },
      { text: 'Nummer (start)', value: 'subscriberNumber', filterable: false },
      { text: 'Blockgröße', value: 'totalSize', filterable: false },
      { text: 'Zugeteilt', value: 'amountAllocated', filterable: false },
      { text: 'Exportiert', value: 'amountExported', filterable: false },
      { text: 'Quarantäne', value: 'amountQuarantined', filterable: false },
      { text: 'Frei', value: 'amountFree', filterable: false },
      {
        text: 'Erste Nummer',
        value: 'subscriberNumberStart',
        filterable: false
      },
      { text: 'Letzte Nummer', value: 'subscriberNumberEnd', filterable: false }
    ],
    aggregatedTableHeaders: [
      { text: 'Vorwahl', value: 'nationalDestinationCode', filterable: true },
      { text: 'Gesamtgröße', value: 'totalSize', filterable: false },
      { text: 'Zugeteilt', value: 'amountAllocated', filterable: false },
      { text: 'Exportiert', value: 'amountExported', filterable: false },
      { text: 'Quarantäne', value: 'amountQuarantined', filterable: false },
      { text: 'Frei', value: 'amountFree', filterable: false },
      { text: 'Frei %', value: 'amountFreePercentage', filterable: false }
    ],
    loading: false
  }),
  computed: {
    aggregatedNumberList() {
      return Object.values(
        this.numberBlockList.reduce((prev, cur, index) => {
          const ndc = cur.nationalDestinationCode;
          if (!Object.keys(prev).includes(ndc.toString())) {
            prev[ndc] = {
              key: ndc,
              nationalDestinationCode: ndc,
              subscriberNumbers: [
                {
                  subscriberNumber: cur.subscriberNumber,
                  subscriberNumberEnd: cur.subscriberNumberEnd
                }
              ],
              totalSize: cur.totalSize,
              amountAllocated: cur.amountAllocated,
              amountExported: cur.amountExported,
              amountQuarantined: cur.amountQuarantined,
              amountFree: cur.amountFree
            };
          } else {
            prev[ndc].subscriberNumbers.push({
              subscriberNumber: cur.subscriberNumber,
              subscriberNumberEnd: cur.subscriberNumberEnd
            });
            prev[ndc].totalSize += cur.totalSize;
            prev[ndc].amountAllocated += cur.amountAllocated;
            prev[ndc].amountExported += cur.amountExported;
            prev[ndc].amountQuarantined += cur.amountQuarantined;
            prev[ndc].amountFree += cur.amountFree;
          }

          return prev;
        }, {})
      ).map((ndc) => {
        ndc.amountFreePercentage =
          Math.round((100 / ndc.totalSize) * ndc.amountFree * 100) / 100;
        return ndc;
      });
    },
    isDownloadPossible() {
      return (
        (!this.showAggregated && this.numberBlockList?.length > 0) ||
        (this.showAggregated && this.aggregatedNumberList?.length > 0)
      );
    }
  },
  mounted() {
    HTTP.get('ocs/number-block').then(({ status, data }) => {
      this.numberBlockList = data.map((e) => {
        e.key = `${e.nationalDestinationCode}${e.subscriberNumber}`;
        return e;
      });
    });
  },
  methods: {
    downloadCurrentTable() {
      if (this.showAggregated) {
        this.downloadAggregatedOcsBlockList();
      } else {
        this.downloadOcsBlockList();
      }
    },
    downloadOcsBlockList() {
      this.downloadStringAsFile(
        this.numberBlockList,
        `rnbReport-${new Date().toISOString().slice(0, 10)}.csv`,
        'text/csv;charset=utf-8;'
      );
    },
    downloadAggregatedOcsBlockList() {
      this.downloadStringAsFile(
        this.aggregatedNumberList,
        `aggregatedRnbReport-${new Date().toISOString().slice(0, 10)}.csv`,
        'text/csv;charset=utf-8;'
      );
    },
    objectToCsv: convertObjectArrayToCsv,
    downloadStringAsFile: function (arr, delimiter) {
      const headers = Object.keys(arr[0]);

      const filteredData = arr.filter(
        (v) =>
          this.tableSearchTerm === '' ||
          this.ndcExtendedSearch(
            v.nationalDestinationCode,
            this.tableSearchTerm,
            v
          )
      );

      const keyIndex = headers.indexOf('key');
      if (keyIndex > -1) {
        headers.splice(keyIndex, 1);
      }

      downloadStringAsFile(
        this.objectToCsv(filteredData, ';'),
        delimiter,
        headers
      );
    },
    ndcExtendedSearch(value, search, item) {
      const numSearch = Number.parseInt(search);
      if (value === null || search === null || isNaN(numSearch)) {
        return false;
      }

      const keys = Object.keys(item);
      if (!keys.includes('nationalDestinationCode')) return false;

      return (
        this.searchNumber(keys, search, numSearch, item) ||
        this.searchNumbers(keys, search, numSearch, item)
      );
    },
    searchNumber(keys, search, numSearch, item) {
      if (keys.includes('subscriberNumber')) {
        const fullNumber = `${item.nationalDestinationCode}${item.subscriberNumberStart}`;
        if (fullNumber.indexOf(search) !== -1) return true;

        const min = Number.parseInt(fullNumber);
        const max = Number.parseInt(
          `${item.nationalDestinationCode}${item.subscriberNumberEnd}`
        );
        return numSearch >= min && numSearch <= max;
      }
      return false;
    },
    searchNumbers(keys, search, numSearch, item) {
      if (keys.includes('subscriberNumbers')) {
        for (const n of item.subscriberNumbers) {
          const number = `${item.nationalDestinationCode}${n.subscriberNumber}`;

          if (number.indexOf(search) !== -1) {
            return true;
          }

          if (
            numSearch >=
              Number.parseInt(
                `${item.nationalDestinationCode}${n.subscriberNumber}`
              ) &&
            numSearch <=
              Number.parseInt(
                `${item.nationalDestinationCode}${n.subscriberNumberEnd}`
              )
          ) {
            return true;
          }
        }

        return false;
      }
    },
    percentColor(percent) {
      return percent < 10 ? 'red' : percent < 20 ? 'orange' : '';
    },
    showToast: function (message, type = 'error') {
      this.$store.commit(
        'addNotification',
        new NotificationObject(type, message)
      );
    }
  }
};
</script>
